// src/ctxReflector/components/Instances/views/Licensed/LicensedPage.tsx
import React, { useState, useEffect, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";

// Tabla genérica
import { GenericTable } from "@ui-components/Table/GenericTable";

// Botones, inputs, modales, alertas
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericAlert from "@ui-components/Alert/GenericAlert";

// Iconos
import { ReactComponent as IconEdit } from "@assets/Icons/Icon_Edit.svg";
import { ReactComponent as IconDelete } from "@assets/Icons/Icon_Delete.svg";

// Hook de Axios con Auth0
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";

// Interfaz + columnas
import { LicensedInstance, columns as baseColumns } from "./LicensedData";

// Requests (ya actualizados)
import {
  fetchLicensedInstances as getAllInstances,
  updateLicensedInstance as updateInstance,
  deleteLicensedInstance as removeLicense,
} from "./request/request";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericSearchbar, {
  ParsedFilter,
} from "@ui-components/Searchbar/GenericSearchbar";
import { makeLicensedFields } from "./licensedFieldSpec";
import {
  buildPredicate,
  FilterField,
  labelId,
  parseToken,
} from "kontrolEdge/helpers/advancedFilters";
import { SkeletonTableRows } from "@ui-components/Skeleton/GenericSkeleton";
import GenericButton from "@ui-components/Button/GenericButton";

function LicensedPage() {
  // Con esto todavía verificas si el user está logueado o si la app está cargando.
  // Lo puedes seguir usando para condicionar la UI si gustas.
  const { isAuthenticated, isLoading } = useAuth0();
  const { tenantId } = useAppSelector((state) => state.selection);

  // <-- Aquí obtenemos la instancia de Axios con token "automático".
  const axiosInstance = useAxiosAuth0();

  // Lista de instancias
  const [instances, setInstances] = useState<LicensedInstance[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Filtro local con GenericSearchbar
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);
  const licensedFields = useMemo(() => makeLicensedFields(), []);
  // Edit modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editInstance, setEditInstance] = useState<LicensedInstance | null>(
    null
  );
  const [originalDescription, setOriginalDescription] = useState("");
  const [newDescription, setNewDescription] = useState("");

  // Delete confirm
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState("");
  const [instanceToDelete, setInstanceToDelete] =
    useState<LicensedInstance | null>(null);

  // Alerta final
  const [isResultAlertOpen, setIsResultAlertOpen] = useState(false);
  const [alertVariant, setAlertVariant] = useState<"success" | "error">(
    "success"
  );
  const [alertTitle, setAlertTitle] = useState("");
  const [alertDescription, setAlertDescription] = useState("");


  // 1) Cargar la lista cuando el usuario está logueado y la app no está en loading
  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    loadInstances();
  }, [isLoading, isAuthenticated, tenantId]);

  const loadInstances = async () => {
    try {
      setIsFetching(true);
      const resp = await getAllInstances(axiosInstance, tenantId);
      setInstances(resp.data || []);
    } catch (error: unknown) {
      console.error("Error fetching Instances =>", error);
    } finally {
      setIsFetching(false);
    }
  };

  // Generate suggested keys from field labels
  const suggestedKeys = useMemo(() => {
    return licensedFields.map((f) => f.label);
  }, [licensedFields]);

  // Build maps for filter normalization
  const fieldById = useMemo(() => {
    const m = new Map<string, FilterField<LicensedInstance>>();
    for (const f of licensedFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      m.set(id, f);
    }
    return m;
  }, [licensedFields]);

  const keyMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of licensedFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      const label = f.label.toLowerCase();
      m.set(id, id);
      m.set(label, id);
    }
    return m;
  }, [licensedFields]);

  // Normalize filters
  const normalizedFilters = useMemo(() => {
    return activeFilters.map((f) => {
      // If searchbar provided an op (live typing), keep it
      const token = f.op
        ? { ...f }
        : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

      // Map key to canonical id
      if (token.key) {
        const k = token.key.trim().toLowerCase();
        token.key = keyMap.get(k) ?? labelId(token.key);
      }

      // If this is a STRING field and user didn't explicitly ask for exact,
      const field = token.key ? fieldById.get(token.key) : undefined;
      if (field?.type === "string") {
        const quoted = token.value.match(/^"(.*)"$/);
        if (quoted) {
          token.value = quoted[1]; // strip quotes
          token.op = "=";
        } else {
          token.op = ":"; // default to contains
        }
      }
      return token;
    });
  }, [activeFilters, keyMap, fieldById]);

  // Build predicate using advancedFilters system
  const predicate = useMemo(
    () => buildPredicate<LicensedInstance>(normalizedFilters, licensedFields),
    [normalizedFilters, licensedFields]
  );

  // Apply predicate to filter data
  const filteredInstances = useMemo(
    () => instances.filter(predicate),
    [instances, predicate]
  );

  // 2) Editar una instancia
  const handleEditClick = (rowData: LicensedInstance) => {
    setEditInstance(rowData);
    setOriginalDescription(rowData.description || "");
    setNewDescription(rowData.description || "");
    setIsModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editInstance) return;
    try {
      setIsSaving(true);

      await updateInstance(axiosInstance, editInstance.id, tenantId, {
        description: newDescription,
      });

      await loadInstances();

      setAlertVariant("success");
      setAlertTitle("Instance updated");
      setAlertDescription(
        `La instancia "${editInstance.name}" fue actualizada correctamente.`
      );
      setIsModalOpen(false);
      setIsResultAlertOpen(true);
    } catch (err: unknown) {
      console.error("Error updating instance =>", err);
      setAlertVariant("error");
      setAlertTitle("Update error");
      setAlertDescription(
        "No se pudo actualizar la instancia, intente de nuevo."
      );
      setIsResultAlertOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  const isDisabled = newDescription === originalDescription;

  // 3) Eliminar una instancia
  const handleDeleteClick = (rowData: LicensedInstance) => {
    setInstanceToDelete(rowData);
    setDeleteConfirmInput("");
    setIsDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!instanceToDelete) return;
    if (deleteConfirmInput.trim().toLowerCase() !== "confirm") {
      setIsDeleteConfirmOpen(false);
      showLicenseSafe();
      return;
    }

    setIsDeleteConfirmOpen(false);
    try {
      setIsFetching(true);
      await removeLicense(axiosInstance, instanceToDelete.id, tenantId);

      setInstances((prev) =>
        prev.filter((inst) => inst.id !== instanceToDelete.id)
      );

      setAlertVariant("success");
      setAlertTitle("Deleted!");
      setAlertDescription(
        `La instancia "${instanceToDelete.name}" se ha eliminado.`
      );
    } catch (err: unknown) {
      console.error("Error removing license =>", err);
      setAlertVariant("error");
      setAlertTitle("Error");
      setAlertDescription(
        "No se pudo eliminar la instancia, intente de nuevo."
      );
    } finally {
      setIsFetching(false);
      setInstanceToDelete(null);
      setIsResultAlertOpen(true);
    }
  };

  const showLicenseSafe = () => {
    setAlertVariant("success");
    setAlertTitle("Your License is safe!");
    setAlertDescription("No se realizó ningún cambio.");
    setIsResultAlertOpen(true);
  };

  // Columnas: agregamos el Edit y Delete como antes
  const finalColumns = baseColumns.map((col) => {
    if (col.header === "Actions" && col.renderCell) {
      return {
        ...col,
        renderCell: (row: LicensedInstance) => (
          <div className="flex items-center gap-2">
            <button
              className="
                                border border-semantic-border-secondary
                                rounded
                                bg-semantic-bg-primary
                                text-semantic-fg-secondary
                                hover:bg-semantic-state-active
                                w-10 h-10 flex items-center justify-center
                            "
              onClick={() => handleEditClick(row)}
            >
              <IconEdit className="w-6 h-6" />
            </button>

            <button
              className="
                                border border-semantic-border-secondary
                                rounded
                                bg-semantic-bg-danger-secondary
                                text-semantic-fg-danger
                                hover:bg-red-100
                                w-10 h-10 flex items-center justify-center
                            "
              onClick={() => handleDeleteClick(row)}
            >
              <IconDelete className="w-5 h-5" />
            </button>
          </div>
        ),
      };
    }
    return col;
  });

  // Render final
  return (
    <div className="relative p-4">
      <div className="pb-6">
        <GenericCard
          isCollapsible={false}
          isLoading={isLoading}
          bodyContent={
            <>
              {/* Filter + Refresh en la misma fila */}
              <div className="flex items-center mb-4 gap-2">
                <div className="w-full max-w-full sm:max-w-[500px]">
                  <GenericSearchbar
                    placeholder="Find by attribute or value (e.g., Name: CTX-R)"
                    suggestedKeys={suggestedKeys}
                    onFiltersChange={setActiveFilters}
                    liveFiltering={true}
                    debounceMs={200}
                  />
                </div>
              </div>

              {/* Tabla */}
                {isFetching ? (
                    <div className="p-4">
                        <SkeletonTableRows
                            rows={5}
                            cols={5}
                            colWidths={["200px"]}
                            rowHeight={15}
                            rowGapClassName="space-y-3"
                        />
                    </div>
                ) : (
                                                
              <GenericTable
                columns={finalColumns}
                data={filteredInstances}
                initialRowsPerPage={10}
                rowsPerPageOptions={[5, 10, 20]}
                width="100%"
                tableClassName="bg-semantic-bg-primary"
              />
            )}
            </>
          }
        />
      </div>

      {/* Modal Edit */}
      <GenericModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Edit Instance"
        size="md"
      >
        <div className="mb-4">
          <GenericInput
            placeholder="Instance description"
            value={newDescription}
            onChange={setNewDescription}
            label="Description"
          />
        </div>

        <div className="flex justify-end space-x-2">
        <div className="flex justify-end space-x-2">
          <GenericButton
            label="Cancel"
            variant="secondary"
            size="md"
            onClick={() => setIsModalOpen(false)}
            disabled={isSaving}
          />
          <GenericButton
            label={isSaving ? "Saving..." : "Save"}
            variant="brand"
            size="md"
            onClick={handleSaveEdit}
            disabled={isDisabled || isSaving}
            loading={isSaving}
          />
        </div>
        </div>
      </GenericModal>

      {/* ALERT: Delete confirm */}
      <GenericAlert
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Are you sure?"
        description="Once deleted, you will not be able to recover this License! Enter 'confirm' to continue..."
        cancelLabel="Cancel"
        confirmLabel="Yes, delete it!"
        variant="warning"
        confirmButtonDisabled={
          deleteConfirmInput.trim().toLowerCase() !== "confirm"
        }
      >
        <div className="mt-4">
          <GenericInput
            placeholder="confirm"
            value={deleteConfirmInput}
            onChange={setDeleteConfirmInput}
          />
        </div>
      </GenericAlert>

      {/* ALERT: Resultado */}
      <GenericAlert
        isOpen={isResultAlertOpen}
        onClose={() => setIsResultAlertOpen(false)}
        onConfirm={() => setIsResultAlertOpen(false)}
        title={alertTitle}
        description={alertDescription}
        cancelLabel="Close"
        confirmLabel="OK"
        variant={alertVariant}
      />
    </div>
  );
}

export default LicensedPage;
